import React, { createContext, useContext, useCallback, useMemo } from "react";
import {
  InfiniteData,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";
import ticketsClient, { CreateTicketInput } from "../../../api/tickets";
import { Ticket, Subtask, TicketComment, Attachment } from "../types";
import {
  TICKETS_QUERY_KEY,
  TICKET_FACETS_QUERY_KEY,
  TICKET_DETAIL_QUERY_KEY,
  ticketDetailQueryKey,
} from "../hooks/useTicketQueries";

type TicketPage = { tickets: Ticket[]; total: number };
type TicketUpdater = (ticket: Ticket) => Ticket;

// Filtering and paging happen on the server, so one ticket lives in as many
// cached pages as there are open views. An optimistic edit patches all of them
// plus the by-id cache the detail views read.
function patchCachedTicket(
  queryClient: QueryClient,
  ticketId: string,
  updater: TicketUpdater,
): void {
  queryClient.setQueriesData<InfiniteData<TicketPage>>(
    { queryKey: TICKETS_QUERY_KEY },
    (old) =>
      old && {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          tickets: page.tickets.map((ticket) =>
            ticket.id === ticketId ? updater(ticket) : ticket,
          ),
        })),
      },
  );
  queryClient.setQueryData<Ticket>(
    ticketDetailQueryKey(ticketId),
    (old) => old && updater(old),
  );
}

function removeCachedTicket(queryClient: QueryClient, ticketId: string): void {
  queryClient.setQueriesData<InfiniteData<TicketPage>>(
    { queryKey: TICKETS_QUERY_KEY },
    (old) =>
      old && {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          tickets: page.tickets.filter((ticket) => ticket.id !== ticketId),
          total: Math.max(page.total - 1, 0),
        })),
      },
  );
  queryClient.removeQueries({ queryKey: ticketDetailQueryKey(ticketId) });
}

type TicketsContextValue = {
  createTicket: (input: CreateTicketInput) => Promise<Ticket>;
  updateTicket: (
    id: string,
    updates: Partial<Ticket>,
    options?: { silent?: boolean },
  ) => Promise<void>;
  deleteTicket: (id: string) => Promise<void>;
  addComment: (ticketId: string, comment: TicketComment) => Promise<void>;
  addSubtask: (ticketId: string, subtask: Subtask) => Promise<void>;
  toggleSubtask: (ticketId: string, subtaskId: string) => Promise<void>;
  updateSubtask: (
    ticketId: string,
    subtaskId: string,
    updates: { title: string },
  ) => Promise<void>;
  deleteSubtask: (ticketId: string, subtaskId: string) => Promise<void>;
  reorderSubtasks: (ticketId: string, subtaskIds: string[]) => Promise<void>;
  addAttachment: (ticketId: string, attachment: Attachment) => Promise<void>;
  deleteAttachment: (ticketId: string, attachmentId: string) => Promise<void>;
  invalidate: () => Promise<void>;
};

const TicketsContext = createContext<TicketsContextValue | null>(null);

export function TicketsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: TICKET_FACETS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: TICKET_DETAIL_QUERY_KEY }),
    ]);
  }, [queryClient]);

  const optimisticUpdate = useCallback(
    async (
      ticketId: string,
      updater: TicketUpdater,
      apiCall: () => Promise<unknown>,
      shouldInvalidate = true,
    ) => {
      patchCachedTicket(queryClient, ticketId, updater);
      try {
        await apiCall();
        if (shouldInvalidate) await invalidate();
      } catch (error) {
        await invalidate();
        throw error;
      }
    },
    [queryClient, invalidate],
  );

  const value = useMemo<TicketsContextValue>(
    () => ({
      createTicket: async (input) => {
        const created = await ticketsClient.createTicket(input);
        await invalidate();
        return created;
      },

      updateTicket: async (id, updates, options) =>
        optimisticUpdate(
          id,
          (ticket) => {
            const now = new Date().toISOString();
            const statusChanged =
              updates.status !== undefined && updates.status !== ticket.status;
            return {
              ...ticket,
              ...updates,
              updatedAt: now,
              ...(statusChanged && { statusChangedAt: now }),
            };
          },
          () => ticketsClient.updateTicket(id, updates),
          !options?.silent,
        ),

      deleteTicket: async (id) => {
        removeCachedTicket(queryClient, id);
        try {
          await ticketsClient.deleteTicket(id);
        } finally {
          await invalidate();
        }
      },

      addComment: async (ticketId, comment) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({ ...ticket, comments: [...ticket.comments, comment] }),
          () => ticketsClient.addComment(ticketId, comment),
        ),

      addSubtask: async (ticketId, subtask) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({ ...ticket, subtasks: [...ticket.subtasks, subtask] }),
          () => ticketsClient.addSubtask(ticketId, subtask),
        ),

      toggleSubtask: async (ticketId, subtaskId) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({
            ...ticket,
            subtasks: ticket.subtasks.map((subtask) =>
              subtask.id === subtaskId
                ? { ...subtask, completed: !subtask.completed }
                : subtask,
            ),
          }),
          () => ticketsClient.toggleSubtask(ticketId, subtaskId),
        ),

      updateSubtask: async (ticketId, subtaskId, updates) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({
            ...ticket,
            subtasks: ticket.subtasks.map((subtask) =>
              subtask.id === subtaskId ? { ...subtask, ...updates } : subtask,
            ),
          }),
          () => ticketsClient.updateSubtask(ticketId, subtaskId, updates),
        ),

      deleteSubtask: async (ticketId, subtaskId) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({
            ...ticket,
            subtasks: ticket.subtasks.filter(
              (subtask) => subtask.id !== subtaskId,
            ),
          }),
          () => ticketsClient.deleteSubtask(ticketId, subtaskId),
        ),

      reorderSubtasks: async (ticketId, subtaskIds) =>
        optimisticUpdate(
          ticketId,
          (ticket) => {
            const subtaskMap = new Map(
              ticket.subtasks.map((subtask) => [subtask.id, subtask]),
            );
            const reordered = subtaskIds
              .map((id) => subtaskMap.get(id))
              .filter((subtask): subtask is Subtask => subtask !== undefined);
            return { ...ticket, subtasks: reordered };
          },
          () => ticketsClient.reorderSubtasks(ticketId, subtaskIds),
          false,
        ),

      addAttachment: async (ticketId, attachment) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({
            ...ticket,
            attachments: [...ticket.attachments, attachment],
          }),
          () => ticketsClient.addAttachment(ticketId, attachment),
        ),

      deleteAttachment: async (ticketId, attachmentId) =>
        optimisticUpdate(
          ticketId,
          (ticket) => ({
            ...ticket,
            attachments: ticket.attachments.filter(
              (attachment) => attachment.id !== attachmentId,
            ),
          }),
          () => ticketsClient.deleteAttachment(ticketId, attachmentId),
        ),

      invalidate,
    }),
    [queryClient, invalidate, optimisticUpdate],
  );

  return (
    <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>
  );
}

export function useTickets(): TicketsContextValue {
  const context = useContext(TicketsContext);
  if (!context) {
    throw new Error("useTickets must be used within a TicketsProvider");
  }
  return context;
}
